/**
 * useCrewRemoval — bấm × gỡ người ở khối ê-kíp (màn Lịch, màn Đơn) đi qua hộp
 * chọn lý do (QĐ 0424). Thợ chụp / trang điểm mở hộp; hậu kỳ gỡ thẳng như cũ
 * (máy chủ không hỏi lý do vai hậu kỳ).
 *
 * Trả `askRemove` để gắn vào onRemove, và `dialog` để đặt trong khối — một
 * chỗ giữ trạng thái cho cả hai màn, không chép hai bản.
 */
import { useState, type ReactNode } from "react";

import type { Order } from "@/features/orders/api";
import { RemoveCrewReasonDialog } from "./components/RemoveCrewReasonDialog";
import { useEkipAssign, type EkipField, type EkipMember } from "./useEkipAssign";

const ROLE_LABEL: Record<EkipField, string> = {
  photographer: "Thợ chụp",
  makeup: "Trang điểm",
  retouch: "Hậu kỳ",
};

/** "Trang điểm · 08:00 ngày 08/10 · khách Huong Vu" — bỏ phần đơn chưa có. */
export function removalContext(order: Pick<Order, "customer" | "shootDate" | "startTime">, field: EkipField): string {
  const parts = [ROLE_LABEL[field]];
  const time = (order.startTime ?? "").slice(0, 5);
  const ymd = (order.shootDate ?? "").slice(0, 10);
  const day = /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}` : "";
  if (time && day) parts.push(`${time} ngày ${day}`);
  else if (day) parts.push(`ngày ${day}`);
  if (order.customer) parts.push(`khách ${order.customer}`);
  return parts.join(" · ");
}

export function useCrewRemoval(order: Order): {
  askRemove: (field: EkipField, person: EkipMember) => void;
  dialog: ReactNode;
} {
  const { removeMember, isPending } = useEkipAssign();
  const [target, setTarget] = useState<{ field: EkipField; person: EkipMember } | null>(null);

  const askRemove = (field: EkipField, person: EkipMember) => {
    if (field === "retouch") {
      void removeMember(order.id, field, person);
      return;
    }
    setTarget({ field, person });
  };

  const dialog = (
    <RemoveCrewReasonDialog
      personName={target ? target.person.name || target.person.id : null}
      context={target ? removalContext(order, target.field) : ""}
      pending={isPending}
      onOpenChange={(open) => {
        if (!open) setTarget(null);
      }}
      onConfirm={async (code, note) => {
        if (!target) return;
        const ok = await removeMember(order.id, target.field, target.person, { code, note });
        if (ok) setTarget(null);
      }}
    />
  );
  return { askRemove, dialog };
}
